/**
 * InstrumentInspector — deep-link page for /place-a-trade/instrument/:symbol
 *
 * Displays full instrument detail, chart placeholder, and inline order panel
 * with Dual Key modal state synced to ?verifyKey=true.
 */
import { useEffect, useState, useMemo, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { api, fmtKES, formatApiErrorDetail } from "@/lib/api";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { CurrencyValue } from "@/components/CurrencyValue";
import { toast } from "sonner";
import { logURLEvent } from "@/lib/logURLEvent";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AiOrderReviewModal } from "@/components/terminal/AiOrderReviewModal";
import { AdvancedExitPanel } from "@/components/terminal/AdvancedExitPanel";
import BreakdownPatternBanner from "@/components/beco/BreakdownPatternBanner";
import InstrumentChartWidget from "@/components/chart/InstrumentChartWidget";
import { useAppCopy } from "@/lib/app-i18n";
import {
    ArrowLeft, TrendingUp, TrendingDown, ShieldCheck, X,
    Loader2, Globe2, Crosshair,
} from "lucide-react";

const INSTITUTIONAL_TIERS = new Set(["advanced", "moe_institution", "cma_institution"]);

function formatReopenTime(iso) {
    if (!iso) return "Monday";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-GB", {
            weekday: "short", day: "2-digit", month: "short",
            hour: "2-digit", minute: "2-digit", timeZone: "UTC",
        }) + " UTC";
    } catch {
        return "Monday";
    }
}

function FundRow({ label, value, tone }) {
    const color = tone === "up" ? "text-success" : tone === "down" ? "text-danger" : tone === "zero" ? "text-zero" : "";
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-sm font-semibold font-mono proportional-nums ${color}`}>{value ?? "—"}</div>
        </div>
    );
}

export default function InstrumentInspector() {
    const { symbol } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { currency, formatMoney, rates } = useCurrency();
    const { t: tApp } = useAppCopy();
    const [instrument, setInstrument] = useState(null);
    const [account, setAccount] = useState(null);
    const [positions, setPositions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [qty, setQty] = useState(1);
    const [side, setSide] = useState("buy");
    const [stopLossKes, setStopLossKes] = useState("");
    const [takeProfitKes, setTakeProfitKes] = useState("");
    const [rationale1, setRationale1] = useState("");
    const [rationale2, setRationale2] = useState("");
    const [busy, setBusy] = useState(false);
    const [dualKeyCode, setDualKeyCode] = useState("");
    const [aiReviewDraft, setAiReviewDraft] = useState(null);
    // Directive 3 · TiF Router
    const [orderType, setOrderType] = useState("market");
    const [tif, setTif] = useState("GTC");
    const [triggerPrice, setTriggerPrice] = useState("");
    const [triggerPriceMode, setTriggerPriceMode] = useState("price"); // 'price' | 'pct'
    const [stopLossMode, setStopLossMode] = useState("pct"); // 'amount' | 'pct'
    const [takeProfitMode, setTakeProfitMode] = useState("pct"); // 'amount' | 'pct'
    // Directive 13 · Advanced Exit (OCO) deep-linking
    const [ocoAccess, setOcoAccess] = useState(false);
    const [ocoPanelPosition, setOcoPanelPosition] = useState(null);
    const [ocoDraft, setOcoDraft] = useState(null);
    // Live Mansa fundamentals for the current instrument (equities only).
    const [fundamentals, setFundamentals] = useState(null);
    const [fundamentalsLoading, setFundamentalsLoading] = useState(false);
    // LeadAfrik country profile(s) for the resolved country/countries.
    const [countryProfiles, setCountryProfiles] = useState([]);
    const [countryProfilesLoading, setCountryProfilesLoading] = useState(false);

    const isInstitutional = INSTITUTIONAL_TIERS.has(user?.tier);
    const verifyKey = searchParams.get("verifyKey") === "true";

    // Log deep-link navigation
    useEffect(() => {
        logURLEvent("NAVIGATION_DEEP_LINK", `/place-a-trade/instrument/${symbol}`, {
            symbol: symbol || "",
        });
    }, [symbol]);

    const reloadAccountAndPositions = useCallback(async () => {
        try {
            const [{ data: accts }, { data: pos }] = await Promise.all([
                api.get("/accounts"), api.get("/positions?status=open"),
            ]);
            setAccount(accts?.[0] || null);
            setPositions(pos || []);
        } catch { /* best-effort refresh */ }
    }, []);

    useEffect(() => {
        const load = async () => {
            try {
                const [{ data: accts }, { data: pos }, eq, idx, fx, comm, tickersResp] = await Promise.all([
                    api.get("/accounts").catch(() => ({ data: [] })),
                    api.get("/positions?status=open").catch(() => ({ data: [] })),
                    api.get("/macro/all-equities").then((r) => r.data).catch(() => []),
                    api.get("/macro/indices").then((r) => r.data).catch(() => []),
                    api.get("/macro/forex").then((r) => r.data).catch(() => []),
                    api.get("/macro/commodities").then((r) => r.data).catch(() => []),
                    api.get("/market/tickers").then((r) => r.data).catch(() => null),
                ]);
                setAccount(accts?.[0] || null);
                setPositions(pos || []);
                // Search for the instrument by symbol (may be exchange-prefixed like NGX-DANGCEM)
                const sym = (symbol || "").toUpperCase();
                const parts = sym.split("-");
                const match = eq.find((e) => {
                    const s = (e.symbol || "").toUpperCase();
                    if (s === sym) return true;
                    if (parts.length >= 2 && s === parts.slice(1).join("-") && (e.exchange || "").toUpperCase() === parts[0]) return true;
                    return false;
                });
                // Indices/FX/commodities aren't exchange-prefixed — match on
                // the raw (non-uppercased) symbol too since FX pairs like
                // "USD/MAD" and index names are case-sensitive.
                const otherMatch = !match && [...(idx || []), ...(fx || []), ...(comm || [])].find((e) => {
                    const s = (e.symbol || "").toUpperCase();
                    return s === sym || e.symbol === symbol;
                });
                const base = match || otherMatch || { symbol: sym, name: sym, price: 0 };
                // The Mansa catalog price (browsing) and the tick-engine's live
                // tradeable price (/market/tickers, what /orders actually fills
                // against) are two independent feeds — prefer the live one here
                // so the on-screen Notional matches what will actually execute.
                const liveTicker = tickersResp?.tickers?.find((t) => t.symbol === base.symbol);
                let resolved = liveTicker
                    ? { ...base, price: liveTicker.price, change: liveTicker.change, change_pct: liveTicker.change_pct, market_open: liveTicker.market_open, market_reopens_at: liveTicker.market_reopens_at }
                    : base;
                // Sept 2026 — prefer the direct live Mansa quote endpoint (a
                // real-time hit against Mansa, not the hourly-synced mirror
                // above) when it has fresh coverage for this symbol; falls
                // back silently to `resolved` above otherwise (most symbols
                // aren't in Mansa's per-symbol quote map yet).
                const liveQuote = await api.get(`/market/live/${encodeURIComponent(base.symbol)}`).then((r) => r.data).catch(() => null);
                if (liveQuote?.available) {
                    resolved = { ...resolved, price: liveQuote.price, change: liveQuote.change, change_pct: liveQuote.changePct };
                }
                setInstrument(resolved);
            } catch {
                toast.error("Failed to load instrument data");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [symbol]);

    const openDualKey = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.set("verifyKey", "true");
        setSearchParams(next, { replace: false });
        logURLEvent("DUAL_KEY_TRIGGER", `/place-a-trade/instrument/${symbol}`, { verifyKey: "true" });
    }, [searchParams, setSearchParams, symbol]);

    // Reopen reminder — one-tap "notify me when this reopens" for a
    // weekend-frozen instrument.
    const [reminderActive, setReminderActive] = useState(false);
    const [reminderBusy, setReminderBusy] = useState(false);
    useEffect(() => {
        if (!instrument || instrument.market_open !== false) return;
        api.get(`/market/reopen-reminders/${instrument.symbol}`)
            .then(({ data }) => setReminderActive(!!data.active))
            .catch(() => {});
    }, [instrument?.symbol, instrument?.market_open]);

    async function toggleReopenReminder() {
        if (!instrument) return;
        setReminderBusy(true);
        try {
            if (reminderActive) {
                await api.delete(`/market/reopen-reminders/${instrument.symbol}`);
                setReminderActive(false);
                toast.success("Reminder cancelled");
            } else {
                await api.post("/market/reopen-reminders", { symbol: instrument.symbol });
                setReminderActive(true);
                toast.success(`We'll notify you when ${instrument.symbol} reopens`);
            }
        } catch (e) {
            toast.error(formatApiErrorDetail(e) || "Could not update reminder");
        } finally {
            setReminderBusy(false);
        }
    }


    const closeDualKey = useCallback(() => {
        const next = new URLSearchParams(searchParams);
        next.delete("verifyKey");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    // Directive 13 · Advanced Exit (OCO) deep-linking — writes
    // ?order=advanced-oco&step=safety-nets on open, restores it on a
    // fresh page load (shared/bookmarked link) if the trader still holds
    // a matching long position.
    useEffect(() => {
        api.get("/oco/access").then(({ data }) => setOcoAccess(Boolean(data.can_create))).catch(() => {});
    }, []);

    useEffect(() => {
        if (searchParams.get("order") !== "advanced-oco" || !instrument?.symbol) return;
        const held = positions.find((p) => p.symbol === instrument.symbol && p.side === "buy" && p.status === "open");
        if (held) setOcoPanelPosition(held);
    }, [searchParams, positions, instrument]);

    const openAdvancedExit = useCallback((position) => {
        setOcoPanelPosition(position);
        setOcoDraft(null);
        const next = new URLSearchParams(searchParams);
        next.set("order", "advanced-oco");
        next.set("step", "safety-nets");
        setSearchParams(next, { replace: false });
    }, [searchParams, setSearchParams]);

    const closeAdvancedExit = useCallback(() => {
        setOcoPanelPosition(null);
        setOcoDraft(null);
        const next = new URLSearchParams(searchParams);
        next.delete("order");
        next.delete("step");
        setSearchParams(next, { replace: true });
    }, [searchParams, setSearchParams]);

    const holdsThis = positions.some((p) => p.symbol === (instrument?.symbol || "") && p.side === "buy");

    // Live Mansa fundamentals — only meaningful for exchange-listed equities
    // ("MANSA" is the synthetic placeholder exchange used for indices/commodities).
    useEffect(() => {
        const exch = instrument?.exchange;
        const ticker = instrument?.symbol;
        if (!exch || exch === "MANSA" || !ticker) {
            setFundamentals(null);
            return;
        }
        let cancelled = false;
        setFundamentalsLoading(true);
        api.get(`/market/live/exchange/${exch}/stocks/${ticker}/fundamentals`)
            .then(({ data }) => { if (!cancelled) setFundamentals(data); })
            .catch(() => { if (!cancelled) setFundamentals({ available: false }); })
            .finally(() => { if (!cancelled) setFundamentalsLoading(false); });
        return () => { cancelled = true; };
    }, [instrument?.exchange, instrument?.symbol]);

    // LeadAfrik country profile(s) — resolved server-side from the instrument
    // symbol (equity country / FX quote-currency country); empty for
    // indices/commodities and any non-LeadAfrik-covered market.
    useEffect(() => {
        const sym = instrument?.symbol;
        if (!sym) {
            setCountryProfiles([]);
            return;
        }
        let cancelled = false;
        setCountryProfilesLoading(true);
        api.get(`/market/live/country-profile/${sym}`)
            .then(({ data }) => { if (!cancelled) setCountryProfiles(data?.profiles || []); })
            .catch(() => { if (!cancelled) setCountryProfiles([]); })
            .finally(() => { if (!cancelled) setCountryProfilesLoading(false); });
        return () => { cancelled = true; };
    }, [instrument?.symbol]);

    // Derived price levels / KES amounts for trigger price / stop-loss /
    // take-profit — each field supports either an absolute amount (in the
    // trader's selected display currency) or a percentage. % semantics:
    // trigger price = % above/below current market price (breakout vs dip
    // entry); stop-loss/take-profit = % move in the INSTRUMENT'S PRICE
    // from entry (side-aware). The price level is the primary derived
    // value now — the KES figure is only a display-only projection of
    // that price level for this position's qty. The actual order sends
    // the raw % (see executeBuyOrder) so the backend derives the real
    // stop_loss_price/take_profit_price itself — never a KES-loss floor.
    const triggerPriceKes = useMemo(() => {
        if (!triggerPrice) return null;
        const p = Number(instrument?.price || 0);
        if (triggerPriceMode === "pct") {
            const pct = Number(triggerPrice) / 100;
            return orderType === "stop" ? p * (1 + pct) : p * (1 - pct);
        }
        const num = Number(triggerPrice);
        return currency === "KES" ? num : num * (rates[currency] || 1);
    }, [triggerPrice, triggerPriceMode, orderType, instrument, currency, rates]);

    const stopLossPriceLevel = useMemo(() => {
        if (!stopLossKes || stopLossMode !== "pct") return null;
        const p = Number(instrument?.price || 0);
        const pct = Number(stopLossKes) / 100;
        return side === "buy" ? p * (1 - pct) : p * (1 + pct);
    }, [stopLossKes, stopLossMode, instrument, side]);

    const takeProfitPriceLevel = useMemo(() => {
        if (!takeProfitKes || takeProfitMode !== "pct") return null;
        const p = Number(instrument?.price || 0);
        const pct = Number(takeProfitKes) / 100;
        return side === "buy" ? p * (1 + pct) : p * (1 - pct);
    }, [takeProfitKes, takeProfitMode, instrument, side]);

    const stopLossKesAbs = useMemo(() => {
        if (stopLossMode === "pct") {
            if (stopLossPriceLevel == null) return null;
            return Math.abs(Number(instrument?.price || 0) - stopLossPriceLevel) * qty;
        }
        if (!stopLossKes) return null;
        const num = Number(stopLossKes);
        return currency === "KES" ? num : num * (rates[currency] || 1);
    }, [stopLossKes, stopLossMode, stopLossPriceLevel, instrument, qty, currency, rates]);

    const takeProfitKesAbs = useMemo(() => {
        if (takeProfitMode === "pct") {
            if (takeProfitPriceLevel == null) return null;
            return Math.abs(takeProfitPriceLevel - Number(instrument?.price || 0)) * qty;
        }
        if (!takeProfitKes) return null;
        const num = Number(takeProfitKes);
        return currency === "KES" ? num : num * (rates[currency] || 1);
    }, [takeProfitKes, takeProfitMode, takeProfitPriceLevel, instrument, qty, currency, rates]);

    // Visual SL/TP guide lines on the instrument chart — prefers the
    // trader's actual open position (real committed levels); falls back to
    // the live order-ticket draft so the guide appears while sizing a new
    // trade, before it's placed.
    const chartRiskLevels = useMemo(() => {
        const heldPos = positions.find(
            (p) => p.symbol === instrument?.symbol && p.side === "buy" && p.status === "open",
        );
        if (heldPos && (heldPos.stop_loss_price || heldPos.take_profit_price)) {
            return {
                entryPrice: heldPos.entry_price,
                stopLossPrice: heldPos.stop_loss_price,
                takeProfitPrice: heldPos.take_profit_price,
            };
        }
        if (side === "buy" && (stopLossPriceLevel || takeProfitPriceLevel)) {
            return {
                entryPrice: Number(instrument?.price || 0) || undefined,
                stopLossPrice: stopLossPriceLevel || undefined,
                takeProfitPrice: takeProfitPriceLevel || undefined,
            };
        }
        return {};
    }, [positions, instrument, side, stopLossPriceLevel, takeProfitPriceLevel]);

    // BUY -> open the mandatory Sentient Desk review modal. SELL -> close
    // the matching held long directly (no review needed for exits).
    const requestOrder = useCallback(() => {
        if (!account) return toast.error("No trading account available.");
        if (side === "sell") {
            const held = positions.find((p) => p.symbol === instrument?.symbol && p.side === "buy" && p.status === "open");
            if (!held) return toast.error(`You don't hold any ${instrument?.symbol}.`);
            setBusy(true);
            api.post("/orders/close", { position_id: held.id })
                .then(({ data }) => {
                    toast.success(`Closed ${instrument?.symbol} · PnL ${fmtKES(data.pnl)}`);
                    reloadAccountAndPositions();
                })
                .catch((err) => toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Order failed"))
                .finally(() => setBusy(false));
            return;
        }
        const cleanRationales = [rationale1, rationale2].map((r) => r.trim()).filter((r) => r.length >= 5);
        if (orderType !== "market" && (!triggerPrice || Number(triggerPrice) <= 0)) {
            return toast.error("Enter a trigger price for a Limit/Stop order.");
        }
        setAiReviewDraft({
            symbol: instrument?.symbol, side: "buy", qty: Number(qty), accountId: account.id,
            stopLoss: stopLossKesAbs ? Number(stopLossKesAbs.toFixed(2)) : null,
            takeProfit: takeProfitKesAbs ? Number(takeProfitKesAbs.toFixed(2)) : null,
            stopLossPct: stopLossMode === "pct" && stopLossKes ? Number(stopLossKes) : null,
            takeProfitPct: takeProfitMode === "pct" && takeProfitKes ? Number(takeProfitKes) : null,
            rationales: cleanRationales,
            orderType, tif, triggerPrice: triggerPriceKes ? Number(triggerPriceKes.toFixed(2)) : null,
        });
    }, [account, side, positions, instrument, qty, stopLossKesAbs, takeProfitKesAbs, stopLossMode, takeProfitMode, stopLossKes, takeProfitKes, rationale1, rationale2, orderType, tif, triggerPriceKes]);

    const handleSubmit = () => {
        if (side === "buy" && account?.dual_key_enabled) {
            openDualKey();
        } else {
            requestOrder();
        }
    };

    const handleDualKeyConfirm = () => {
        closeDualKey();
        requestOrder();
        setDualKeyCode("");
    };

    async function executeBuyOrder(reviewToken) {
        const draft = aiReviewDraft;
        if (!draft) return;
        setBusy(true);
        try {
            if (draft.orderType && draft.orderType !== "market") {
                const payload = {
                    symbol: draft.symbol, qty: draft.qty, account_id: draft.accountId,
                    order_type: draft.orderType, tif: draft.tif, trigger_price: draft.triggerPrice,
                    review_token: reviewToken,
                };
                if (draft.stopLossPct) payload.stop_loss_pct = draft.stopLossPct;
                else if (draft.stopLoss) payload.stop_loss_kes = Number(draft.stopLoss);
                if (draft.takeProfitPct) payload.take_profit_pct = draft.takeProfitPct;
                else if (draft.takeProfit) payload.take_profit_kes = Number(draft.takeProfit);
                if (draft.rationales?.length) payload.rationales = draft.rationales;
                const { data } = await api.post("/orders/pending", payload);
                if (data.status === "filled") {
                    toast.success(`${draft.orderType.toUpperCase()} order filled immediately (IOC): ${draft.qty} ${draft.symbol}`);
                } else if (data.status === "cancelled") {
                    toast.error(`Order not placed: ${data.reason || "cancelled"}`);
                } else {
                    toast.success(`${draft.orderType.toUpperCase()} order resting (${draft.tif}) — will fill at ${formatMoney(draft.triggerPrice, "KES")}.`);
                }
                setAiReviewDraft(null);
                await reloadAccountAndPositions();
                return;
            }
            const payload = { symbol: draft.symbol, side: "buy", qty: draft.qty, account_id: draft.accountId, review_token: reviewToken };
            if (draft.stopLossPct) payload.stop_loss_pct = draft.stopLossPct;
            else if (draft.stopLoss) payload.stop_loss_kes = Number(draft.stopLoss);
            if (draft.takeProfitPct) payload.take_profit_pct = draft.takeProfitPct;
            else if (draft.takeProfit) payload.take_profit_kes = Number(draft.takeProfit);
            if (draft.rationales?.length) payload.rationales = draft.rationales;
            const { data } = await api.post("/orders", payload);
            toast.success(`BUY filled: ${draft.qty} ${draft.symbol} @ ${data.execution.fill_price} · fee KES ${data.fee_kes ?? 0}`);
            setAiReviewDraft(null);
            await reloadAccountAndPositions();
        } catch (e) {
            if (e?.response?.status === 428) {
                setAiReviewDraft(null);
                toast.error("Your desk review expired — please retry the order.");
                return;
            }
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Order rejected — check your balance and drawdown limits.");
        } finally { setBusy(false); }
    }

    const price = Number(instrument?.price || 0);
    const changePct = Number(instrument?.change_pct || 0);
    const notional = price * qty;
    const sym = instrument?.symbol || symbol || "";

    const widgets = useMemo(() => [
        {
            id: "instrument-detail", title: sym, category: "Info",
            layout: { x: 0, y: 0, w: 8, h: 16 }, minW: 5, minH: 10, noPadding: true,
            render: () => (
                <div className="p-5 space-y-4" data-testid="instrument-detail-widget">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="flex items-center gap-2 mb-1">
                                <Globe2 className="w-5 h-5 text-primary" />
                                <h2 className="font-heading text-h2 leading-tight">{sym}</h2>
                                {instrument?.exchange && (
                                    <Badge variant="outline" className="text-[10px]">{instrument.exchange}</Badge>
                                )}
                            </div>
                            <div className="text-sm text-muted-foreground">{instrument?.name || ""}</div>
                        </div>
                        <div className="text-right">
                            <div className="text-3xl font-mono font-bold">
                                <CurrencyValue amount={price} baseCurrency={instrument?.currency || "KES"} size="md" testId="instrument-price" />
                            </div>
                            <div className={`flex items-center gap-1 justify-end text-sm ${toneClassName(signTone(changePct))}`}>
                                {signTone(changePct) === "neg" ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                                {changePct.toFixed(2)}%
                            </div>
                        </div>
                    </div>
                    {instrument?.market_open === false && (
                        <div
                            className="flex flex-wrap items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-600 dark:text-amber-400"
                            data-testid="inst-market-closed-banner"
                        >
                            <Globe2 className="w-3.5 h-3.5" />
                            <span>Markets closed — price is frozen at Friday's close. Reopens {formatReopenTime(instrument.market_reopens_at)}.</span>
                            <Button
                                type="button"
                                size="sm"
                                variant={reminderActive ? "secondary" : "outline"}
                                className="h-6 text-xs px-2 ml-auto"
                                onClick={toggleReopenReminder}
                                disabled={reminderBusy}
                                data-testid="inst-reopen-reminder-btn"
                            >
                                {reminderActive ? "✓ Reminder set" : "Notify me when this reopens"}
                            </Button>
                        </div>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                        {instrument?.volume != null && (
                            <div className="border border-border rounded-lg p-2">
                                <div className="text-muted-foreground">Volume</div>
                                <div className="font-mono font-semibold">{Number(instrument.volume).toLocaleString()}</div>
                            </div>
                        )}
                        {instrument?.market_cap != null && (
                            <div className="border border-border rounded-lg p-2">
                                <div className="text-muted-foreground">Market Cap</div>
                                <div className="font-mono font-semibold">{Number(instrument.market_cap).toLocaleString()}</div>
                            </div>
                        )}
                        {instrument?.sector && (
                            <div className="border border-border rounded-lg p-2">
                                <div className="text-muted-foreground">Sector</div>
                                <div className="font-semibold">{instrument.sector}</div>
                            </div>
                        )}
                        {instrument?.currency && (
                            <div className="border border-border rounded-lg p-2">
                                <div className="text-muted-foreground">Currency</div>
                                <div className="font-semibold">{instrument.currency}</div>
                            </div>
                        )}
                    </div>
                    <InstrumentChartWidget symbol={sym} riskLevels={chartRiskLevels} />
                </div>
            ),
        },
        {
            id: "instrument-order", title: "Order Entry", category: "Trading",
            layout: { x: 8, y: 0, w: 4, h: 10 }, minW: 3, minH: 7, noPadding: true,
            render: () => (
                <div className="p-4 space-y-3 overflow-y-auto max-h-full" data-testid="instrument-order-widget">
                    <div className="flex gap-2">
                        <Button
                            variant={side === "buy" ? "default" : "outline"}
                            size="sm"
                            onClick={() => setSide("buy")}
                            className={side === "buy" ? "bg-emerald-600 hover:bg-emerald-700 flex-1" : "flex-1"}
                            data-testid="inst-order-buy"
                        >
                            Buy
                        </Button>
                        <Button
                            variant={side === "sell" ? "default" : "outline"}
                            size="sm"
                            onClick={() => setSide("sell")}
                            className={side === "sell" ? "bg-rose-600 hover:bg-rose-700 flex-1" : "flex-1"}
                            data-testid="inst-order-sell"
                        >
                            Sell
                        </Button>
                    </div>
                    <div>
                        <label className="text-xs text-muted-foreground">Quantity</label>
                        <Input
                            type="number" min={1} value={qty}
                            onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                            className="h-8" data-testid="inst-order-qty"
                        />
                    </div>
                    {side === "buy" && (
                        <>
                            <div className="grid grid-cols-3 gap-1.5">
                                {["market", "limit", "stop"].map((t) => (
                                    <Button key={t} type="button" variant={orderType === t ? "default" : "outline"} size="sm"
                                        className="h-9 text-xs" onClick={() => setOrderType(t)} data-testid={`inst-order-type-${t}`}>
                                        {t[0].toUpperCase() + t.slice(1)}
                                    </Button>
                                ))}
                            </div>
                            <div className="rounded-md border border-border bg-muted/30 px-2.5 py-2 text-xs text-muted-foreground leading-snug" data-testid="inst-order-type-tutorial">
                                {tApp(`tutorial.order.${orderType}`, "")}
                            </div>
                            {orderType !== "market" && (
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="text-xs text-muted-foreground">Trigger price</label>
                                            <div className="flex text-[9px] rounded border border-border overflow-hidden">
                                                <button type="button" onClick={() => setTriggerPriceMode("price")}
                                                    className={`px-1.5 py-0.5 ${triggerPriceMode === "price" ? "bg-primary text-primary-foreground" : ""}`}
                                                    data-testid="inst-trigger-mode-price">{currency}</button>
                                                <button type="button" onClick={() => setTriggerPriceMode("pct")}
                                                    className={`px-1.5 py-0.5 ${triggerPriceMode === "pct" ? "bg-primary text-primary-foreground" : ""}`}
                                                    data-testid="inst-trigger-mode-pct">%</button>
                                            </div>
                                        </div>
                                        <Input type="number" min={0} value={triggerPrice}
                                            onChange={(e) => setTriggerPrice(e.target.value)}
                                            className="h-8"
                                            placeholder={triggerPriceMode === "pct" ? "e.g. 3 (%)" : `e.g. 15.50 (${currency})`}
                                            data-testid="inst-trigger-price" />
                                        {triggerPriceMode === "pct" && triggerPriceKes > 0 && (
                                            <div className="text-[10px] text-muted-foreground mt-1" data-testid="inst-trigger-price-preview">
                                                ≈ {formatMoney(triggerPriceKes, "KES")}
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="text-xs text-muted-foreground">Time in Force</label>
                                        <select value={tif} onChange={(e) => setTif(e.target.value)}
                                            className="h-8 w-full rounded-md border border-border bg-background text-sm px-2"
                                            data-testid="inst-tif-select">
                                            <option value="GTC">GTC</option>
                                            <option value="DAY">Day</option>
                                            <option value="IOC">IOC</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label className="text-xs text-muted-foreground">Stop-loss — required</label>
                                    <div className="flex text-[9px] rounded border border-border overflow-hidden">
                                        <button type="button" onClick={() => setStopLossMode("amount")}
                                            className={`px-1.5 py-0.5 ${stopLossMode === "amount" ? "bg-primary text-primary-foreground" : ""}`}
                                            data-testid="inst-sl-mode-amount">{currency}</button>
                                        <button type="button" onClick={() => setStopLossMode("pct")}
                                            className={`px-1.5 py-0.5 ${stopLossMode === "pct" ? "bg-primary text-primary-foreground" : ""}`}
                                            data-testid="inst-sl-mode-pct">%</button>
                                    </div>
                                </div>
                                <Input
                                    type="number" min={0} value={stopLossKes}
                                    onChange={(e) => setStopLossKes(e.target.value)}
                                    className="h-8"
                                    placeholder={stopLossMode === "pct" ? "e.g. 5 (%)" : `e.g. 500 (${currency} loss)`}
                                    data-testid="inst-order-stop-loss"
                                />
                                {stopLossMode === "pct" && stopLossKesAbs > 0 && (
                                    <div className="text-[10px] text-muted-foreground mt-1" data-testid="inst-sl-preview">
                                        Max loss ≈ {formatMoney(stopLossKesAbs, "KES")}
                                    </div>
                                )}
                            </div>
                            {isInstitutional && (
                                <div>
                                    <div className="flex items-center justify-between mb-1">
                                        <label className="text-xs text-muted-foreground">Take-profit — optional</label>
                                        <div className="flex text-[9px] rounded border border-border overflow-hidden">
                                            <button type="button" onClick={() => setTakeProfitMode("amount")}
                                                className={`px-1.5 py-0.5 ${takeProfitMode === "amount" ? "bg-primary text-primary-foreground" : ""}`}
                                                data-testid="inst-tp-mode-amount">{currency}</button>
                                            <button type="button" onClick={() => setTakeProfitMode("pct")}
                                                className={`px-1.5 py-0.5 ${takeProfitMode === "pct" ? "bg-primary text-primary-foreground" : ""}`}
                                                data-testid="inst-tp-mode-pct">%</button>
                                        </div>
                                    </div>
                                    <Input
                                        type="number" min={0} value={takeProfitKes}
                                        onChange={(e) => setTakeProfitKes(e.target.value)}
                                        className="h-8"
                                        placeholder={takeProfitMode === "pct" ? "e.g. 10 (%)" : `e.g. 1000 (${currency} gain)`}
                                        data-testid="inst-order-take-profit"
                                    />
                                    {takeProfitMode === "pct" && takeProfitKesAbs > 0 && (
                                        <div className="text-[10px] text-muted-foreground mt-1" data-testid="inst-tp-preview">
                                            Est. gain ≈ {formatMoney(takeProfitKesAbs, "KES")}
                                        </div>
                                    )}
                                </div>
                            )}
                            {isInstitutional && (
                                <div className="space-y-1.5">
                                    <label className="text-xs text-muted-foreground">Trade rationale — 2 required</label>
                                    <Input value={rationale1} onChange={(e) => setRationale1(e.target.value)} className="h-8" placeholder="Rationale 1" data-testid="inst-order-rationale-1" />
                                    <Input value={rationale2} onChange={(e) => setRationale2(e.target.value)} className="h-8" placeholder="Rationale 2" data-testid="inst-order-rationale-2" />
                                </div>
                            )}
                            {holdsThis && ocoAccess && (
                                <Button
                                    variant="outline" size="sm" className="w-full h-11 min-h-[44px]"
                                    onClick={() => openAdvancedExit(positions.find((p) => p.symbol === sym && p.side === "buy" && p.status === "open"))}
                                    data-testid="inst-open-advanced-exit"
                                >
                                    Advanced Exit (OCO)
                                </Button>
                            )}
                        </>
                    )}
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Notional</span>
                        <CurrencyValue amount={notional} className="font-mono font-semibold text-foreground" testId="inst-order-notional" />
                    </div>
                    {instrument?.market_open === false && side === "sell" && holdsThis && (
                        <div
                            className="text-[11px] text-muted-foreground leading-snug flex items-start gap-1.5"
                            data-testid="inst-sell-weekend-guard-note"
                        >
                            <ShieldCheck className="w-3 h-3 mt-0.5 shrink-0 text-amber-500" />
                            You can't close this position until {formatReopenTime(instrument.market_reopens_at)} — that's a real exchange rule (no trading of any kind while the market is shut), not a bug.
                        </div>
                    )}
                    <Button
                        className="w-full"
                        onClick={handleSubmit}
                        disabled={busy || (side === "sell" && !holdsThis) || instrument?.market_open === false}
                        data-testid="inst-order-submit"
                        title={instrument?.market_open === false ? `Market closed — reopens ${formatReopenTime(instrument.market_reopens_at)}` : undefined}
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                        {instrument?.market_open === false ? "Market closed" : `${side === "buy" ? "Review & Buy" : "Sell"} ${sym}`}
                    </Button>
                    {account && (
                        <div className="text-[10px] text-muted-foreground text-center mt-1">
                            Balance: <CurrencyValue amount={account.current_balance} showToggle={false} />
                        </div>
                    )}
                </div>
            ),
        },
        {
            id: "instrument-fundamentals", title: "Fundamentals", category: "Info",
            layout: { x: 8, y: 10, w: 4, h: 8 }, minW: 3, minH: 5, noPadding: true,
            render: () => (
                <div className="p-4 space-y-3" data-testid="instrument-fundamentals-widget">
                    {fundamentalsLoading ? (
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading fundamentals…
                        </div>
                    ) : !fundamentals || fundamentals.available === false ? (
                        <div className="text-xs text-muted-foreground" data-testid="fundamentals-unavailable">
                            Fundamentals not available for this instrument.
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            <FundRow label="Name" value={fundamentals.name} />
                            <FundRow label="Sector" value={fundamentals.sector} />
                            <FundRow label="Currency" value={fundamentals.currency} />
                            <FundRow label="Country" value={fundamentals.country} />
                            <FundRow label="Price" value={fundamentals.price != null ? Number(fundamentals.price).toLocaleString() : "—"} />
                            <FundRow
                                label="Change %"
                                value={fundamentals.change_pct != null ? `${signedPrefix(fundamentals.change_pct)}${Number(fundamentals.change_pct).toFixed(2)}%` : "—"}
                                tone={fundamentals.change_pct != null ? signTone(fundamentals.change_pct) : undefined}
                            />
                            <FundRow label="Market Cap" value={fundamentals.market_cap != null ? Number(fundamentals.market_cap).toLocaleString() : "—"} />
                            <FundRow label="Volume" value={fundamentals.volume != null ? Number(fundamentals.volume).toLocaleString() : "—"} />
                        </div>
                    )}
                </div>
            ),
        },
        {
            id: "instrument-country-profile", title: "Country Profile", category: "Info",
            layout: { x: 8, y: 18, w: 4, h: 8 }, minW: 3, minH: 5, noPadding: true,
            render: () => (
                <div className="p-4 space-y-3" data-testid="instrument-country-profile-widget">
                    {countryProfilesLoading ? (
                        <div className="text-xs text-muted-foreground flex items-center gap-2">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading country profile…
                        </div>
                    ) : countryProfiles.length === 0 ? (
                        <div className="text-xs text-muted-foreground" data-testid="country-profile-unavailable">
                            Country profile not available for this instrument.
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {countryProfiles.map((p) => (
                                <div key={p.iso2} className="space-y-2">
                                    <div className="flex items-center gap-2 text-sm font-semibold">
                                        <span aria-hidden="true">{p.flag}</span>
                                        <span>{p.name || p.iso2}</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                        <FundRow label="Region" value={p.region} />
                                        <FundRow label="Currency" value={p.currency} />
                                        <FundRow label="ISO-2" value={p.iso2} />
                                        <FundRow label="ISO-3" value={p.iso3} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ),
        },
    ], [instrument, sym, price, changePct, side, qty, notional, busy, account, openDualKey, handleSubmit,
        stopLossKes, takeProfitKes, rationale1, rationale2, isInstitutional, holdsThis,
        orderType, tif, triggerPrice, ocoAccess, openAdvancedExit, positions,
        triggerPriceMode, stopLossMode, takeProfitMode, triggerPriceKes, stopLossKesAbs, takeProfitKesAbs,
        currency, formatMoney, fundamentals, fundamentalsLoading, countryProfiles, countryProfilesLoading]);

    if (loading) {
        return (
            <div className="p-6 flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading instrument...
            </div>
        );
    }

    return (
        <div className="pb-20 md:pb-0" data-testid="instrument-inspector-page">
            <BreakdownPatternBanner />
            <div className="flex items-center gap-3 mb-4">
                <Button variant="ghost" size="sm" onClick={() => navigate("/app/place-a-trade")} data-testid="inst-back-btn">
                    <ArrowLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <div className="overline text-primary flex items-center gap-2">
                    <Crosshair className="w-4 h-4" /> {sym}
                </div>
            </div>

            <DashboardGridWrapper
                role="TRADER"
                viewId={`instrument-${(sym || "unknown").toLowerCase()}`}
                widgets={widgets}
                className="mb-3"
            />

            {/* Dual Key verification modal — synced to ?verifyKey=true */}
            {verifyKey && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" data-testid="dual-key-modal">
                    <div className="bg-card border border-border rounded-xl p-6 w-full max-w-sm space-y-4">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <ShieldCheck className="w-5 h-5 text-primary" />
                                <h3 className="font-bold">Dual Key Verification</h3>
                            </div>
                            <button onClick={closeDualKey} data-testid="dual-key-close">
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <p className="text-xs text-muted-foreground">
                            Enter your peer verification code to confirm this {side} order for {qty}x {sym}.
                        </p>
                        <Input
                            placeholder="Peer code"
                            value={dualKeyCode}
                            onChange={(e) => setDualKeyCode(e.target.value)}
                            data-testid="dual-key-input"
                        />
                        <Button className="w-full" onClick={handleDualKeyConfirm} disabled={busy} data-testid="dual-key-confirm">
                            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                            Confirm Order
                        </Button>
                    </div>
                </div>
            )}

            <AiOrderReviewModal
                open={Boolean(aiReviewDraft)}
                draft={aiReviewDraft}
                onClose={() => setAiReviewDraft(null)}
                onConfirm={executeBuyOrder}
                busy={busy}
            />

            <AdvancedExitPanel
                open={Boolean(ocoPanelPosition)}
                position={ocoPanelPosition}
                currentPrice={price}
                draft={ocoDraft}
                onDraftChange={setOcoDraft}
                onClose={closeAdvancedExit}
                onCreated={reloadAccountAndPositions}
                canCreate={ocoAccess}
            />
        </div>
    );
}
